import React from "react";
import { ClosedCaptioning } from "@phosphor-icons/react";
import { useCaptions } from "../context/CaptionsContext";
import { toast } from "sonner";

/**
 * CCToggleButton — one reusable CC button for every player.
 *
 * Why one button: the user requested CC across MUSIC, VIDEOS, and
 * "everything else". The simplest accessibility win is a single
 * preference + a single button shape that lives wherever audio or
 * video plays. The button only flips the global preference — each
 * player listens to it and decides what to do (force-show YouTube CC,
 * attach a <track> with Whisper-generated VTT, or show the global
 * LiveCaptions band for TTS content).
 *
 * Variants:
 *   • `dark` — for use over a dark video, white-on-translucent
 *   • `light` — for use over a light surface
 *   • `pill` — labeled "CC" pill, wider hit target for accessibility
 *     menus
 */
export default function CCToggleButton({ variant = "dark", testid = "cc-toggle", className = "" }) {
  const { on, toggle } = useCaptions();

  const click = () => {
    toggle();
    toast.success(on ? "Captions off" : "Captions on", { id: "cc-toast" });
  };

  const base =
    variant === "pill"
      ? "inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-[11px] tracking-[0.18em] uppercase font-bold transition-all"
      : variant === "text"
      ? "inline-flex items-center gap-1.5 px-1 h-9 text-[11px] tracking-[0.18em] uppercase font-bold transition-all"
      : "w-9 h-9 grid place-items-center rounded-full transition-all";

  const palette =
    variant === "light"
      ? on
        ? "bg-forest text-cream hover:bg-forest-600"
        : "bg-cream-200 text-ink hover:bg-cream-300"
      : variant === "text"
      ? on
        ? "text-amber-300 hover:text-amber-200"
        : "text-cream/85 hover:text-cream"
      : on
      ? "bg-amber-300 text-ink shadow-md"
      : "bg-white/15 text-cream hover:bg-white/25";

  return (
    <button
      type="button"
      onClick={click}
      data-testid={testid}
      data-cc-on={on ? "1" : "0"}
      data-no-drag="1"
      aria-pressed={on ? "true" : "false"}
      aria-label={on ? "Turn captions off" : "Turn captions on"}
      title={on ? "Captions ON — tap to mute" : "Captions OFF — tap to read along"}
      className={`${base} ${palette} ${className}`}
    >
      <ClosedCaptioning size={variant === "pill" || variant === "text" ? 14 : 16} weight={on ? "fill" : "bold"} />
      {(variant === "pill" || variant === "text") && <span>{on ? "CC on" : "CC"}</span>}
    </button>
  );
}
